import { z } from 'zod';

import {
  type FlowBlock,
  type FlowDefinitionState,
  type FlowDefinitionWithFlow,
  type FlowRunBlockDataSource,
} from './flowDefinition.js';

export enum FlowBlockState {
  Pending = 'Pending',
  Queued = 'Queued',
  Running = 'Running',
  PendingRetry = 'PendingRetry',
  Waiting = 'Waiting',
  Completed = 'Completed',
  Invalidated = 'Invalidated',
  Failed = 'Failed',
}

export enum EntityType {
  Encounter = 'encounter',
  EhrInformation = 'ehrInformation',
  PatientOrder = 'patientOrder',
  ExternalDocumentPacket = 'externalDocumentPacket',
}

export const ActiveFlowBlockStates = new Set([
  FlowBlockState.Pending,
  FlowBlockState.Queued,
  FlowBlockState.Running,
  FlowBlockState.Waiting,
  FlowBlockState.PendingRetry,
]);

export const TerminalFlowBlockStates = new Set([
  FlowBlockState.Completed,
  FlowBlockState.Invalidated,
  FlowBlockState.Failed,
]);

export enum FlowRunState {
  Pending = 'Pending',
  PendingRetry = 'PendingRetry',
  Queued = 'Queued',
  Running = 'Running',
  Completed = 'Completed',
  Failed = 'Failed',
  Canceled = 'Canceled',
}

export const ActiveFlowRunStates = new Set([
  FlowRunState.Pending,
  FlowRunState.PendingRetry,
  FlowRunState.Queued,
  FlowRunState.Running,
]);
export const QueuedFlowRunStates = new Set([
  FlowRunState.Pending,
  FlowRunState.PendingRetry,
  FlowRunState.Queued,
]);
export const TerminalFlowRunStates = new Set([
  FlowRunState.Completed,
  FlowRunState.Failed,
  FlowRunState.Canceled,
]);
export const TerminalFlowRunStatesArray = Array.from(
  TerminalFlowRunStates
) as TerminalFlowRunState[];
export type TerminalFlowRunState =
  | FlowRunState.Completed
  | FlowRunState.Canceled
  | FlowRunState.Failed;
export interface TerminalStateFlowRunSummary extends Omit<FlowRunSummary, 'state'> {
  state: TerminalFlowRunState;
}
export function isTerminalStateFlowRunSummary(
  run: FlowRunSummary
): run is TerminalStateFlowRunSummary {
  return TerminalFlowRunStates.has(run.state);
}

export enum BlockRunDataVariant {
  None = 'None',
  Redacted = 'Redacted',
  AllIncludingPotentialPhi = 'AllIncludingPotentialPhi',
}

export const FindOneByIdQuerySchema = z.object({
  /**
   * To protect patient privacy, we redact PHI from the flow run data.
   * This flag allows the caller to request the full data.
   */
  dataVariant: z.nativeEnum(BlockRunDataVariant).optional(),
});
export type FindOneByIdQuery = z.infer<typeof FindOneByIdQuerySchema>;
export interface RuntimeBlockSerializeOptions {
  blockRunDataVariant: BlockRunDataVariant;
}
export interface FlowRun {
  id: string;
  created: string;
  updated: string;
  flowDefinitionId: string;
  entityId: string | null;
  entityType: string | null; // We are leaving this intentionally as a string to allow for flexible entity types beyond what is typed in EntityType
  startedById: string | null;
  practiceId: string;
  state: FlowRunState;
  error: string | null;
  parentId: string | null;
  parentBlockId: string | null;
  snapshotId: string | null;
  flowDefinitionState: FlowDefinitionState | null;
  groupId: string | null;
  type: FlowRunType;
  startAttempts: number | null;
  lastStartAttemptError: string | null;
  testCaseId?: string | null;
  startDataId?: string | null;
  startedAt: string | null;
  endedAt: string | null;
  reRunFromRunId: string | null;
  priority: number | null;
  fleetId: string | null;
  claimedBy: string | null;
  claimedAt: string | null;
  archivedAt: string | null;
  blockDataArchivalGsPath: string | null;
}

export interface FlowRunWithOrganization extends FlowRun {
  organizationId: string;
  flowId: string;
  agentId: string;
}

export interface FlowRunWithData extends FlowRunWithOrganization {
  data: {
    blocks: Record<string, FlowRunBlockWithData>;
  };
}

export interface FlowRunWithStartedBy extends FlowRun {
  startedByEmail: string | null;
}

export interface EntityFlowRun extends FlowRun {
  agentName: string;
  agentId: string;
  flowName: string;
  flowId: string;
}

export enum FlowRunType {
  Production = 'production',
  Test = 'test',
}

export interface FlowRunFindAllOptions {
  limit?: number;
  offset?: number;
  name?: string;
  organizationId?: string | string[];
  flowId?: string;
  flowDefinitionId?: string;
  createdBefore?: string;
  createdAfter?: string;
  startedBefore?: string;
  startedAfter?: string;
  endedBefore?: string;
  endedAfter?: string;
  startedByEmail?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  state?: FlowRunState | FlowRunState[];
  flowDefinitionState?: FlowDefinitionState | FlowDefinitionState[];
  groupId?: string;
  type?: FlowRunType;
  parentId?: string;
  parentFlowDefinitionBlockId?: string;
  version?: string | string[];
  entityType?: string; // We are leaving this intentionally as a string to allow for flexible entity types beyond what is typed in EntityType
  entityId?: string;
  fleetId?: string;
  id?: string | string[];
}

export type FlowRunSummary = Pick<
  FlowRunWithStartedBy,
  | 'id'
  | 'flowDefinitionId'
  | 'flowDefinitionState'
  | 'created'
  | 'updated'
  | 'entityId'
  | 'entityType'
  | 'startedByEmail'
  | 'state'
  | 'practiceId'
  | 'error'
  | 'type'
  | 'groupId'
  | 'startedAt'
  | 'endedAt'
  | 'reRunFromRunId'
  | 'fleetId'
  | 'archivedAt'
> &
  Pick<FlowDefinitionWithFlow, 'name' | 'organizationId' | 'version'> & {
    groupName?: string;
  };

export interface FlowRunBlock {
  id: string;
  created: string;
  updated: string;
  flowRunId: string;
  blockId: string;
  block: FlowBlock;
  state: FlowBlockState;
  startedAt: string | null;
  endedAt: string | null;
  durationMillis: number | null;

  dependsOnBlockIds: string[];
}

export interface FlowRunBlockWithData extends FlowRunBlock {
  // A JSON serialized value
  error: string | null;

  data: unknown;
  metadata: {
    source?: {
      type: FlowRunBlockDataSource;
    };
    [key: string]: unknown;
  };
  dataSizeBytes: number | null;
}
